import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';

const sidebar = tv({
  base: [
    'hidden h-full min-h-0 w-64 shrink-0 flex-col self-stretch',
    'bg-[var(--color-rail-sidebar-bg)] text-[var(--color-rail-sidebar-text)]',
    'border-r border-white/10 md:flex',
  ].join(' '),
});

const brand = tv({
  base: 'border-b border-white/10 px-4 py-5 text-lg font-semibold tracking-tight',
});

const nav = tv({
  base: [
    'flex-1 overflow-y-auto px-2 py-4',
    // Keep scroll; hide the native scrollbar chrome on the dark rail
    '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
  ].join(' '),
});

export interface EnterpriseSidebarProps {
  readonly brandLabel: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly className?: string;
}

export function EnterpriseSidebar({
  brandLabel,
  children,
  footer,
  className,
}: EnterpriseSidebarProps): JSX.Element {
  const { t } = useTranslation('admin');
  return (
    <aside className={sidebar({ className })} data-testid="enterprise-sidebar">
      <div className={brand()}>{brandLabel}</div>
      <nav className={nav()} aria-label={t('nav.bottom.sideAria')}>
        {children}
      </nav>
      {footer ? <div className="border-t border-white/10 p-3">{footer}</div> : null}
    </aside>
  );
}
